import { IdentityServerConfig } from '@environments/environment-ids.models'

/**
 * Centralized function to generate all API endpoint URLs.
 * This ensures consistency across all environments and makes updates easy.
 * @param apiDomain The base domain for the integrated services API.
 * @param identityServerUrl The URL for the identity server.
 * @param clientId The application's client ID.
 * @param webSocketDomain The domain for the WebSocket service.
 */
export const getApiUrls = (apiDomain: string, identityServerUrl: string, clientId: string, webSocketDomain: string) => {
  const apiPath = `${apiDomain}/`
  const seasonsUrl = `${apiPath}seasons`
  const matchesUrlFn = (matchId: string) => `${apiPath}matches${matchId ? `/${matchId}` : ''}`
  const matchWithContextUrl = (seasonId: string, competitionId: string, matchId: string) =>
    `${apiPath}matches/season/${seasonId}/competition/${competitionId}/match/${matchId}`
  const teamsUrlFn = (seasonId: string, competitionId: string, teamId?: string) =>
    `${apiPath}seasons/${seasonId}/competitions/${competitionId}/teams${teamId ? `/${teamId}` : ''}`
  const playersUrlFn = (seasonId: string, competitionId: string, teamId: string, id?: string) =>
    `${teamsUrlFn(seasonId, competitionId, teamId)}/players${id ? `/${id}` : ''}`
  const userProfileUrl = `${apiPath}userprofile`

  return {
    apiDomain: apiDomain,
    userProfileUrl,
    avatarUrl: `${userProfileUrl}/avatar`,
    seasonsUrl,
    settingsUrl: `${apiPath}settings`,
    translationsUrlFn: (language: string) => `${apiPath}translations/${language}`,
    teamsUrlFn,
    teamOverviewMatchesUrlFn: (seasonId: string, teamId: string) =>
      `${apiPath}seasons/${seasonId}/teams/${teamId}/overviewmatches`,
    teamMetricResultsUrlFn: (seasonId: string, competitionId: string, teamId: string) =>
      `${teamsUrlFn(seasonId, competitionId, teamId)}/metricresults`,
    teamMetricDetailUrlFn: (seasonId: string, competitionId: string, teamId: string, metricKey: string) =>
      `${teamsUrlFn(seasonId, competitionId, teamId)}/metricdetail/${metricKey}`,
    teamsCompareUrl: `${apiPath}teams/compare`,
    matchDaysUrl: `${apiPath}matchdays`,
    playersUrlFn: playersUrlFn,
    playerMetricResultsUrlFn: (seasonId: string, competitionId: string, teamId: string, id: string) =>
      `${playersUrlFn(seasonId, competitionId, teamId, id)}/metricresults`,
    playerMetricDetailUrlFn: (seasonId: string, competitionId: string, teamId: string, id: string, metricKey: string) =>
      `${playersUrlFn(seasonId, competitionId, teamId, id)}/metricdetail/${metricKey}`,
    playersSearchUrl: `${apiPath}players/search`,
    comparePlayersUrl: `${apiPath}players/compare`,
    standingsByMatchDayIdUrlFn: (matchDayId: string) => `${apiPath}matchdays/${matchDayId}/standings`,
    matchesUrlFn: matchesUrlFn,
    lastMatchesUrlFn: (matchId: string) => `${matchesUrlFn(matchId)}/lastnmatches`,
    matchStreamingUrlFn: (matchId: string) => `${matchesUrlFn(matchId)}/streaming`,
    matchPassMatrixUrlFn: (matchId: string) => `${matchesUrlFn(matchId)}/passmatrix`,
    matchTeamMetricDetailUrlFn: (matchId: string, metricKey: string) =>
      `${matchesUrlFn(matchId)}/teams/metricresult/${metricKey}`,
    matchComparePlayersUrlFn: (matchId: string) => `${matchesUrlFn(matchId)}/players/compare`,
    matchPlayerMetricDetailUrlFn: (matchId: string, teamId: string, id: string, metricKey: string) =>
      `${apiPath}matches/${matchId}/teams/${teamId}/players/${id}/metricresult/${metricKey}`,
    matchesBetweenDateUrlFn: (seasonId: string, competitionId: string, dateFrom: string, dateTo: string) =>
      `${seasonsUrl}/${seasonId}/competitions/${competitionId}/matchesbetweendates?dateFrom=${dateFrom}&dateTo=${dateTo}`,
    metricDefinitionsUrl: `${apiPath}metricdefinitions`,
    webSocketUrlFn: (accessToken: string) => `${webSocketDomain}?access_token=${accessToken}`,
    filterMatchesTimeline: (matchId: string) => `${matchesUrlFn(matchId)}/streamevents?videoMetadataType=ALL`,
    matchRefereeUrlFn: (matchId: string) => `${matchesUrlFn(matchId)}/referees`,
    matchKitSelectorUrlFn: (matchId: string) => `${matchesUrlFn(matchId)}/kitselector`,
    matchGreenKeeperUrlFn: (matchId: string) => `${matchesUrlFn(matchId)}/greenkeeper`,
    matchSunlightUrlFn: (matchId: string) => `${matchesUrlFn(matchId)}/sunlight`,
    rankingsUrl: `${apiPath}metrics/rank`,
    licenseAgreementUrlFn: (userId: string) =>
      `${identityServerUrl}/consent/legal?applicationId=${clientId}&userId=${userId}`,

    matchTeamMetricsWithContextUrlFn: (seasonId: string, competitionId: string, matchId: string) =>
      `${matchWithContextUrl(seasonId, competitionId, matchId)}/teammetricresults`,
    matchPlayerListMetricsWithContextUrlFn: (seasonId: string, competitionId: string, matchId: string) =>
      `${matchWithContextUrl(seasonId, competitionId, matchId)}/playerlistmetricresults`,
    matchHeatmapWithContextUrlFn: (seasonId: string, competitionId: string, matchId: string) =>
      `${matchWithContextUrl(seasonId, competitionId, matchId)}/heatmap`,
    matchPlayerWithContextUrlFn: (
      seasonId: string,
      competitionId: string,
      matchId: string,
      teamId: string,
      playerId: string
    ) => `${matchWithContextUrl(seasonId, competitionId, matchId)}/teams/${teamId}/players/${playerId}`,
    assets: `${apiPath}assets`
  }
}

/**
 * Generates configuration settings for Identity Server authentication.
 * This function creates a configuration object that handles both web and native mobile authentication flows,
 * setting up the necessary endpoints and parameters for OAuth2/OpenID Connect authentication.
 *
 * @param {string} identityServerUrl - The base URL of the identity server (e.g., 'https://auth.example.com')
 * @param {string} scheme - The URL scheme for native app deep linking (e.g., 'myapp://')
 * @param {string} clientId - The OAuth client identifier for the application
 * @param {boolean} [isNative=false] - Flag indicating if the app is running in a native mobile environment
 * @param {string} [domain=window.location.origin] - The base domain of the web application
 *
 * @returns {IdentityServerConfig} Identity Server Config Object
 */
export const getIdentityServerConfig = (
  identityServerUrl: string,
  scheme: string,
  clientId: string,
  isNative: boolean = false,
  domain: string = window.location.origin
): IdentityServerConfig => ({
  authorityUrl: identityServerUrl,
  redirectUri: `${isNative ? scheme : domain + '/'}auth`,
  endSessionRedirectUri: `${isNative ? scheme : domain + '/'}login`,
  apiLogout: `${identityServerUrl}/connect/endsession`,
  tagPostLogoutRedirectUri: 'post_logout_redirect_uri',
  endSessionRedirectConcurrencyUri: `${isNative ? scheme : domain + '/'}concurrency`,
  responseType: 'code',
  clientId: clientId,
  scopes: 'openid portal-api integratedservices-api integratednotifications-api'
})
